import { ColorTypes } from '@genial/design-tokens';
import {
  CustomerEntity,
  ErrorListResponse,
  Service,
  instanceOfErrorListResponse,
} from '@genial/services';
import { Utils, useBreakpointValue } from '@genial/utils';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../../../../hooks/useToastCreator';
import { useCustomerActions } from '../../../../../store/customer/actions';
import { customerItemState, customerStatusState } from '../../../../../store/customer/atom';
import { productItemState } from '../../../../../store/product/atom';
import { hasProductItem } from '../../../../../store/product/selectors';
import { SelectedProduct } from '../../../../../store/product/types';
import { defaultErrorMessage } from '../../../constants';
import { customerCaptions, screenName } from '../constants';

import { DesktopCustomerItemSelected } from './desktop';
import { MobileCustomerItemSelected } from './mobile';
import { TabletCustomerItemSelected } from './tablet';

const calculateEstimatedValue = (balance: number, selectedProduct?: SelectedProduct) => {
  const mountEstimatedValue = (isSale: boolean, value: number) => {
    return Utils.Mask.Money(isSale ? balance + value : balance - value);
  };

  if (!selectedProduct) {
    return '';
  }
  const isSale = selectedProduct.operationType === 'sell';
  if (selectedProduct.type === 'Equity') {
    const fullValue = (selectedProduct?.orderValue || 0) * (selectedProduct?.orderQuantity || 0);
    return mountEstimatedValue(isSale, fullValue);
  }
  const value = selectedProduct?.orderValue || 0;
  return mountEstimatedValue(isSale, value);
};

export const ResponsiveCustomerItemSelected: React.FC = () => {
  const customerItem = useRecoilValue(customerItemState);
  const customerStatus = useRecoilValue(customerStatusState);
  const hasProduct = useRecoilValue(hasProductItem);
  const productItem = useRecoilValue(productItemState);

  const { showToast, showToastFromError } = useToastCreator('customer-toas-balance-update');

  const { handleSelectCustomerItem } = useCustomerActions();

  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  const customerStatusIsFinished =
    customerStatus?.status === 'opened' || customerStatus?.status === 'completed';

  const customerCaption =
    customerItem?.current && customerStatusIsFinished ? customerCaptions(customerItem.current) : [];

  const currentCustomerAvailableBalance = customerItem?.current?.availableBalance || 0;
  const estimatedValueCustomer = calculateEstimatedValue(
    currentCustomerAvailableBalance,
    productItem?.current
  );

  const estimatedValueDefaultText = useBreakpointValue({
    base: 'Adicione o prod.',
    sm: 'Adicione o prod.',
    lg: 'Adicione o produto',
  });

  const estimatedValue = hasProduct ? estimatedValueCustomer : estimatedValueDefaultText;

  const handleUpdateBalance = (item: CustomerEntity) => {
    setIsLoadingBalance(true);

    Service.Balance.V2.getByAccountNumber({
      screenName,
      accountNumber: item.accountNumber,
    })
      .then(({ data }) => {
        const availableBalance = data.balance.availableBalance ?? 0;

        handleSelectCustomerItem({ ...item, availableBalance });

        showToast('success', 'Tudo certo!', 'O saldo foi atualizado com sucesso!', {
          duration: 3000,
        });
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessage);
      })
      .finally(() => {
        setIsLoadingBalance(false);
      });
  };

  const backgroundColorBadge = `risk.${(
    customerItem.current.suitabilityProfileName || 'conservador'
  ).toLowerCase()}` as ColorTypes.Option;

  const props = {
    handleUpdateBalance,
    estimatedValue,
    customerCaption,
    isLoadingBalance,
    customerItem: customerItem.current,
    backgroundColorBadge,
  };

  const customerItemSelected = useBreakpointValue({
    base: <MobileCustomerItemSelected {...props} />,
    sm: <TabletCustomerItemSelected {...props} />,
    lg: <DesktopCustomerItemSelected {...props} />,
  });

  return customerItemSelected;
};
